import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Expand, X, ExternalLink } from 'lucide-react';
export default function Demo() {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [email, setEmail] = useState('');
  const [showCustomerDemoForm, setShowCustomerDemoForm] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [isSubmittingCustomer, setIsSubmittingCustomer] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [customerSubmitted, setCustomerSubmitted] = useState(false);
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      alert('Please enter an email address.');
      return;
    }
    try {
      await fetch('https://hooks.zapier.com/hooks/catch/23169540/2v9kcsk/', {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          timestamp: new Date().toISOString(),
          source: 'kraya_demo_access',
          triggered_from: window.location.href
        })
      });
    } catch {
      // fire-and-forget
    }
    setIsUnlocked(true);
  };
  const handleCustomerDemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerEmail.trim()) {
      alert('Please enter your email address to request a customer demo.');
      return;
    }
    setIsSubmittingCustomer(true);
    try {
      await fetch('https://hooks.zapier.com/hooks/catch/23169540/2v9qzq8/', {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: customerEmail,
          timestamp: new Date().toISOString(),
          source: 'kraya_customer_demo_request',
          triggered_from: window.location.href
        })
      });
    } catch {
      // fire-and-forget
    }
    setCustomerEmail('');
    setShowCustomerDemoForm(false);
    setIsSubmittingCustomer(false);
    setCustomerSubmitted(true);
  };
  return <div className="min-h-screen bg-black text-white font-sans" data-id="element-150">
      {/* Header */}
      <header className="p-6 border-b border-gray-800" data-id="element-151">
        <div className="flex justify-between items-center max-w-7xl mx-auto" data-id="element-152">
          <div className="flex items-center gap-3" data-id="element-153">
            <h1 className="text-2xl font-bold" data-id="element-154">Kraya preview</h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-600 text-white" data-id="element-155">
              beta
            </span>
          </div>
          <div className="flex flex-col items-end gap-2" data-id="element-156">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors" data-id="element-157">
              ← Back to Home
            </Link>
            <Link to="/use-cases" className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-158">
              Use Cases
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-6 max-w-7xl mx-auto" data-id="element-159">
        <div className="text-center mb-6" data-id="element-160">
          <h2 className="text-3xl font-bold mb-4" data-id="element-161">Interactive Demo</h2>
          <p className="text-gray-400 text-lg" data-id="element-162">
            Experience KRAYA's capabilities with our interactive demo
          </p>
        </div>

        {/* Demo Container with Overlay */}
        <div className="flex justify-center relative mb-12" data-id="element-163">
          <div className={`${isExpanded ? 'fixed inset-0 z-50 bg-black' : 'w-full max-w-5xl h-[calc(80vh-100px)] rounded-xl border border-gray-800'} overflow-hidden relative transition-all duration-300 shadow-2xl`} data-id="element-164">
            {/* Demo Controls */}
            {isUnlocked && <div className="absolute top-4 right-4 z-10 flex gap-2" data-id="element-165">
                {!isExpanded ? <button onClick={() => setIsExpanded(true)} className="bg-black/70 hover:bg-black/90 text-white p-2.5 rounded-lg backdrop-blur-md transition-colors border border-gray-700" title="Expand to full screen" data-id="element-166">
                    <Expand size={18} data-id="element-167" />
                  </button> : <button onClick={() => setIsExpanded(false)} className="bg-black/70 hover:bg-black/90 text-white p-2.5 rounded-lg backdrop-blur-md transition-colors border border-gray-700" title="Exit full screen" data-id="element-168">
                    <X size={18} data-id="element-169" />
                  </button>}
                <button onClick={() => window.open('https://project-cloning-interface-126.magicpatterns.app/', '_blank', 'width=1200,height=800')} className="bg-black/70 hover:bg-black/90 text-white p-2.5 rounded-lg backdrop-blur-md transition-colors border border-gray-700" title="Open in new window" data-id="element-170">
                  <ExternalLink size={18} data-id="element-171" />
                </button>
              </div>}

            {/* Demo iframe */}
            <iframe src="https://project-cloning-interface-126.magicpatterns.app/" className="w-full h-full border-none bg-white" title="KRAYA Interactive Demo" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-172" />

            {/* Lock Screen Overlay */}
            {!isUnlocked && <div className="absolute inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center" data-id="element-173">
                <div className="text-center p-8 max-w-md w-full" data-id="element-174">
                  <div className="mb-8" data-id="element-175">
                    <h3 className="text-3xl font-bold mb-4" data-id="element-176">Unlock the Demo</h3>
                    <p className="text-gray-400 text-lg" data-id="element-177">
                      Enter your email to access the interactive demo
                    </p>
                  </div>
                  <form onSubmit={handleEmailSubmit} className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl" data-id="element-178">
                    <input type="email" placeholder="Enter your email address" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-5 py-3.5 mb-4 rounded-xl bg-black/50 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" data-id="element-179" />
                    <button type="submit" className="w-full px-6 py-3.5 bg-white text-black rounded-xl hover:bg-gray-200 transition-colors font-bold text-lg" data-id="element-180">
                      Access Demo
                    </button>
                  </form>
                </div>
              </div>}
          </div>
        </div>

        {/* Customer Demo Request Section */}
        {!isExpanded && <div className="flex justify-center pb-12" data-id="element-181">
            <div className="w-full max-w-2xl text-center" data-id="element-182">
              <p className="text-gray-400 text-lg mb-6" data-id="element-183">
                If you want to try the customer demo version of the app -
                contact us
              </p>

              {customerSubmitted ? <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 max-w-md mx-auto shadow-2xl text-center" data-id="element-184">
                  <p className="text-white font-semibold text-lg" data-id="element-185">
                    🎉 Request submitted!
                  </p>
                  <p className="text-gray-400 text-sm mt-2" data-id="element-186">
                    We'll contact you soon to schedule your customer demo.
                  </p>
                </div> : !showCustomerDemoForm ? <button onClick={() => setShowCustomerDemoForm(true)} className="px-8 py-3.5 bg-white text-black rounded-xl hover:bg-gray-200 transition-colors font-bold shadow-lg" data-id="element-187">
                  Request customer demo
                </button> : <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 max-w-md mx-auto shadow-2xl" data-id="element-188">
                  <form onSubmit={handleCustomerDemoSubmit} data-id="element-189">
                    <input type="email" placeholder="Enter your email address" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} className="w-full px-5 py-3.5 mb-4 rounded-xl bg-black/50 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" data-id="element-190" />
                    <div className="flex gap-3" data-id="element-191">
                      <button type="submit" disabled={isSubmittingCustomer} className="flex-1 px-6 py-3.5 bg-white text-black rounded-xl hover:bg-gray-200 transition-colors font-bold disabled:opacity-70" data-id="element-192">
                        {isSubmittingCustomer ? 'Submitting...' : 'Submit'}
                      </button>
                      <button type="button" onClick={() => setShowCustomerDemoForm(false)} className="px-6 py-3.5 bg-gray-800 text-white rounded-xl hover:bg-gray-700 transition-colors font-bold border border-gray-700" data-id="element-193">
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>}
            </div>
          </div>}
      </main>
    </div>;
}